import React from 'react';
import { Upload, Loader2 } from 'lucide-react';
import clsx from 'clsx';

interface DropZoneProps {
    isDragging: boolean;
    uploading: boolean;
    disabled: boolean;
    description: string;
    maxSizeMB: number;
    acceptedTypes: string[];
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onClick: () => void;
    fileInputRef: React.RefObject<HTMLInputElement>;
    onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const DropZone: React.FC<DropZoneProps> = ({
    isDragging,
    uploading,
    disabled,
    description,
    maxSizeMB,
    acceptedTypes,
    onDragOver,
    onDragLeave,
    onDrop,
    onClick,
    fileInputRef,
    onFileSelect
}) => {
    return (
        <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={onClick}
            className={clsx(
                'relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                isDragging && 'border-blue-500 bg-blue-50',
                !isDragging && 'border-gray-300 hover:border-gray-400 hover:bg-gray-50',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptedTypes.join(',')}
                onChange={onFileSelect}
                className="hidden"
                disabled={disabled}
            />

            {uploading ? (
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                    <p className="text-gray-600">Uploading...</p>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-2">
                    <Upload className={clsx(
                        'w-10 h-10',
                        isDragging ? 'text-blue-500' : 'text-gray-400'
                    )} />
                    <p className="text-gray-600">{description}</p>
                    <p className="text-sm text-gray-400">
                        Drag and drop or click to browse • Max {maxSizeMB}MB per file
                    </p>
                </div>
            )}
        </div>
    );
};
